import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Output } from '@angular/core';
import { ActionIconDef, GridActionIconClickEvent, GridConfig, GridHyperlinkClickEvent, GridRow } from '@inavista/components/grid';
import { APP_CONFIG, AppConfig } from 'projects/shared/src/lib/app-config.token';
import { UserRoles } from 'projects/sales/src/app/shared/constants/app.constants';
import { CommonService } from 'projects/sales/src/app/shared/services/common.service';

// Service section → ISC Code → Advanced Search: the business partners that are not an ISC code yet
// (dbo.spGetSoldToBPAdvancedSearch), on @inavista/components. Rows: RIMS Master api/grid/user-profile-search/isc-codes
// (paged in SQL; before, odata/SoldToBP loaded all 177k BPs into the API and needed no sign-in). Picking a row (the arrow,
// or its Business Partner) hands it to the Service section and closes, as before. Excel for every role but Sales Rep.
const COLUMNS = ['soldToBP', 'name', 'telephone', 'email', 'addressCode', 'address'];
const SELECT_ICON = '<line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />';

@Component({
    selector: 'app-advanced-isc-codes-popup',
    templateUrl: './advanced-isc-codes-popup.component.html',
    styleUrls: ['./advanced-isc-codes-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdvancedIscCodesPopupComponent {
  @Output() onISCCodeSelected = new EventEmitter();
  @Output() onClose = new EventEmitter();
  isExpanded = false;
  readonly gridConfig: GridConfig;
  readonly actionIcons: ActionIconDef[] = [{ key: 'select', label: 'Select ISC', icon: SELECT_ICON, colorFn: () => '#000000de' }];

  constructor(@Inject(APP_CONFIG) config: AppConfig, commonService: CommonService) {
    this.gridConfig = {
      apiBaseUrl: `${config.masterApiURL}grid/user-profile-search/isc-codes`,
      keyColumn: 'soldToExtId',
      clientSide: false,
      pageSize: 200,
      rowHeight: 26,
      loadingIndicator: 'banner',
      stretchColumns: true,
      wrapHeaders: true,
      enableSearch: true,
      enableContextMenuFilter: true,
      enableFitColumns: true,
      enableResetLayout: true,
      enableExport: commonService.getUSER()?.role !== UserRoles.salesRep,
      defaultColumnOrder: COLUMNS,
      // The procedure's other columns, never shown before: not offered (Country was hidden and out of the picker).
      hiddenByDefaultColumns: ['soldToExtId', 'type', 'houseNumber', 'city', 'countryId', 'stateProvince', 'zipCode', 'statusName'],
      columnLabels: { soldToBP: 'Business Partner', name: 'Name', telephone: 'Telephone', email: 'Email', addressCode: 'Address Code', address: 'Address' },
      columnWidths: { soldToBP: 110, name: 120, telephone: 100, email: 120, addressCode: 120, address: 120 },
      columnAlign: Object.fromEntries(COLUMNS.map(c => [c, {
        header: { horizontal: 'left' as const },
        ...(c === 'soldToBP' || c === 'addressCode' ? { horizontal: 'center' as const } : {}),
      }])),
      // As before: the address followed by its city, state, zip code and country.
      columnFormatters: {
        address: (_value, row) => [row['address'], row['city'], row['stateProvince'], row['zipCode'], row['countryId']]
          .filter(v => v !== null && v !== undefined && v !== '').join(', '),
      },
      hyperlinkColumns: ['soldToBP'],
      emptyStateMessage: 'No business partner to show.',
      noResultsMessage: 'No business partner matches. Clear the search or the filters to see them all.',
      persistenceKey: 'rims.users.isc-code-search',
      persistenceVersion: 1,
    };
  }

  private pick(row: GridRow) {
    this.onISCCodeSelected.emit(row);
    this.close();
  }

  onActionIconClick(event: GridActionIconClickEvent) {
    if (event.iconKey === 'select') this.pick(event.row);
  }

  onHyperlinkClick(event: GridHyperlinkClickEvent) {
    this.pick(event.row);
  }

  close() {
    this.onClose.emit(true);
  }

  expandCollapse() {
    this.isExpanded = !this.isExpanded;
  }
}
